import React, { useEffect, useRef, useState } from "react"

/**
 * Renders `children` only once the placeholder gets near the viewport, so below-the-fold sections
 * (and the JS / API calls behind them) stay off the critical path.
 */
export const LazyMount = ({
  children,
  fallback,
  className,
}: {
  children: React.ReactNode
  /** Server-rendered until the section mounts, e.g. a `sr-only` heading so crawlers still see it. */
  fallback?: React.ReactNode
  /** Reserves the section's space (`min-h-*`, and its background) until it has mounted. */
  className?: string
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: "200px 0px" }
    )
    observer.observe(el)

    return () => observer.disconnect()
  }, [])

  return (
    <div ref={ref} className={className}>
      {visible ? children : fallback}
    </div>
  )
}
